import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createScaleOnboardingTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. I'm calling to help
          you get started with your Remote Patient Monitoring program. For
          quality and compliance purposes, this call may be recorded. To protect
          your privacy, can you please confirm your full name and date of
          birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'onboardingPurposeReassurance',
      type: 'checkbox',
      title: <p>Onboarding Purpose & Reassurance</p>,
      description: (
        <ScriptWrapper>
          "The goal of today's call is to help you get your scale set up, make
          sure everything is working, and take your first weight reading if
          we're able."
          <br />
          "There's no way to do this wrong — I'll guide you step by step, and if
          something doesn't work right away, that's completely okay."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Purpose explained',
          value: 'purposeExplained',
        },
        {
          key: makeid(10),
          label: 'Reassurance provided',
          value: 'reassuranceProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'earlyGoalSetting',
      type: 'checkbox',
      title: <p>Early Goal Setting (Clinical Best Practice)</p>,
      description: (
        <ScriptWrapper>
          "As part of your care, your clinic recommends checking your weight as
          often as possible, ideally daily."
          <br />
          "For the most consistent and helpful results, it's best to weigh
          yourself at the same time each day — ideally first thing in the
          morning, after using the bathroom, but before eating, drinking, or
          getting dressed."
          <br />
          "If you miss a day, that's okay — consistency over time is what
          matters most."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Daily weight readings encouraged',
          value: 'dailyWeightReadingsEncouraged',
        },
        {
          key: makeid(10),
          label:
            'Best-practice timing explained (morning, after bathroom, before eating/dressing)',
          value: 'bestPracticeTimingExplained',
        },
        {
          key: makeid(10),
          label: 'Flexibility and consistency emphasized',
          value: 'flexibilityAndConsistencyEmphasized',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceReadiness',
      type: 'radio',
      title: <p>Device Readiness Check</p>,
      description: (
        <ScriptWrapper>
          "Do you have the scale with you right now?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Scale present during call',
          value: 'scalePresentDuringCall',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — let's get everything ready together."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Scale not present during call',
          value: 'scaleNotPresentDuringCall',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "That's okay — we can review how to use it today, and you can
                  take your first reading later."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'scaleActivationReviewed',
      type: 'radio',
      title: <p>Scale Activation Reviewed</p>,
      description: (
        <ScriptWrapper>
          "To activate the scale, please turn it over."
          <br />
          "On the back, press and hold the circular button for about 10
          seconds."
          <br />
          "This only needs to be done once."
          <br />
          "Let me know when that's complete."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'One-time activation completed',
          value: 'oneTimeActivationCompleted',
        },
        {
          key: makeid(10),
          label: 'Activation deferred',
          value: 'activationDeferred',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'firstWeightReadingStatus',
      type: 'checkbox',
      title: <p>First Weight Reading & Status</p>,
      description: (
        <ScriptWrapper>
          "Place the scale on a hard, flat surface."
          <br />
          "When you're ready, step onto the scale and stand still."
          <br />
          "Wait until your weight appears on the screen and you hear the voice
          say, 'Reading complete.'"
          <br />
          "Let me know when that happens."
          <br />
          "Do you see PASS or FAIL on the screen?"
          <br />
          If PASS: "Great."
          <br />
          If FAIL: "That's okay — sometimes that just means we need to try
          again."
          <br />
          "Please step off the scale, wait a few seconds, and then step back on
          so we can repeat the reading."
          <br />
          (If needed, MA guides patient to check surface stability, stance, and
          stillness, then repeats.)
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Reading completed successfully',
          value: 'readingCompletedSuccessfully',
        },
        {
          key: makeid(10),
          label: 'PASS displayed',
          value: 'passDisplayed',
        },
        {
          key: makeid(10),
          label: 'FAIL displayed',
          value: 'failDisplayed',
        },
        {
          key: makeid(10),
          label: 'Reading repeated',
          value: 'readingRepeated',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'weightUnitConfirmed',
      type: 'checkbox',
      title: <p>Weight & Unit Confirmed</p>,
      description: (
        <ScriptWrapper>
          "Can you please tell me the weight displayed and the unit of
          measurement?"
          <br />
          Follow-up (Patient-Facing): "Is that typically around your usual
          weight?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Weight value confirmed',
          value: 'weightValueConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-number',
              name: 'weightValue',
              required: false,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unit of measurement confirmed',
          value: 'unitOfMeasurementConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input',
              name: 'weightUnit',
              required: false,
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'weightAlignment',
      type: 'radio',
      title: <p>Weight Alignment</p>,
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient reports reading aligns with typical weight',
          value: 'patientReportsReadingAlignsWithTypicalWeight',
        },
        {
          key: makeid(10),
          label: 'Patient reports reading does not align with typical weight',
          value: 'patientReportsReadingDoesNotAlignWithTypicalWeight',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'programValueSingleUserEducation',
      type: 'checkbox',
      title: <p>Program Value & Single-User Education</p>,
      description: (
        <ScriptWrapper>
          "Your weight readings are securely shared with your care team, who
          review trends over time."
          <br />
          "Even small changes in weight can be important, which is why regular
          readings are helpful."
          <br />
          "It's also important that only you use this scale."
          <br />
          "Readings are sent directly to your physician, so having anyone else
          step on the scale could cause confusion in your care."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Trend monitoring explained',
          value: 'trendMonitoringExplained',
        },
        {
          key: makeid(10),
          label: 'Single-user requirement explained',
          value: 'singleUserRequirementExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we currently have on file for
          you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
          <br />
          If Medicare Advantage: "Which plan is listed?"
          <br />
          "Thank you — I'll confirm what's documented in your chart."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Which plan is listed?"
                  <br />
                  "Thank you — I'll confirm what's documented in your chart."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              type: 'checkbox',
              name: 'insuranceTypeOnFile',
              options: [
                {
                  key: makeid(10),
                  label: 'Insurance type on file confirmed',
                  value: 'insuranceTypeOnFileConfirmed',
                },
              ],
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "Before we continue, I want to confirm the best cell phone number to
          reach you. Is [number on file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone number confirmed',
          value: 'cellPhoneNumberConfirmed',
        },
        {
          key: makeid(10),
          label: 'Cell phone number updated',
          value: 'cellPhoneNumberUpdated',
        },
        {
          key: makeid(10),
          label: 'Patient reports no cell phone',
          value: 'patientReportsNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          "If you have a cell phone, we also offer text messages for reminders
          and updates related to your RPM care."
          <br />
          "Would you like to receive text messages from us?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered and accepted → Enrolled',
          value: 'offeredAndAcceptedEnrolled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — I'll get you enrolled. You can opt out at any time."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Offered and declined',
          value: 'offeredAndDeclined',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "No problem — that's completely optional."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Not eligible — no cell phone',
          value: 'notEligibleNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Next Monthly Check-In</p>,
      description: (
        <ScriptWrapper>
          "As part of the program, a monthly phone call with our team is
          required."
          <br />
          "These calls help us check in, answer questions, and make sure
          everything continues to work smoothly."
          <br />
          "If your weight changes outside of your provider's recommended range,
          your clinic may reach out — often with a call from a nurse — to decide
          if any follow-up is needed."
          <br />
          "Would you like to schedule your first monthly check-in now, or should
          we reach out closer to when it's due?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Requirement reviewed',
          value: 'requirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Value explained',
          value: 'valueExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand everything we discussed
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TScaleOnboardingTemplateValues = ExtractStepValues<
  ReturnType<typeof createScaleOnboardingTemplate>
>;

export const createScaleOnboardingBlurb = (
  data: TScaleOnboardingTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    onboardingPurposeReassurance,
    earlyGoalSetting,
    deviceReadiness,
    scaleActivationReviewed,
    firstWeightReadingStatus,
    weightUnitConfirmed,
    weightValue,
    weightUnit,
    weightAlignment,
    programValueSingleUserEducation,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;

  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    onboardingPurposeReassurance?.length &&
    !hasField(ctx, 'onboardingPurposeReassurance')
  ) {
    addField(ctx, 'onboardingPurposeReassurance');
    const map = {
      purposeExplained: 'Onboarding call purpose explained.',
      reassuranceProvided:
        'Patient reassured regarding scale setup and available support.',
    };
    txt.push(...mapValuesToText(onboardingPurposeReassurance, map));
  }

  if (earlyGoalSetting?.length && !hasField(ctx, 'earlyGoalSetting')) {
    addField(ctx, 'earlyGoalSetting');
    const map = {
      flexibilityAndConsistencyEmphasized:
        'Importance of maintaining consistency with flexibility over time emphasized.',
      dailyWeightReadingsEncouraged:
        'Daily weight monitoring encouraged as a clinical best practice.',
      bestPracticeTimingExplained:
        'Ideal timing for daily weight readings reviewed.',
    };

    txt.push(...mapValuesToText(earlyGoalSetting, map));
  }

  if (deviceReadiness && !hasField(ctx, 'deviceReadiness')) {
    addField(ctx, 'deviceReadiness');
    const map = {
      scalePresentDuringCall: 'present during call',
      scaleNotPresentDuringCall: 'not present during call',
    };

    txt.push(`Scale availability reviewed. Device ${map[deviceReadiness]}.`);

    if (deviceReadiness === 'scaleNotPresentDuringCall') {
      txt.push(
        'Patient provided with education and instructed to complete first reading at a latertime.',
      );
    }
  }

  if (scaleActivationReviewed && !hasField(ctx, 'scaleActivationReviewed')) {
    addField(ctx, 'scaleActivationReviewed');
    const map = {
      oneTimeActivationCompleted: 'completed',
      activationDeferred: 'deferred',
    };
    txt.push(
      `Scale activation reviewed. One-time activation ${map[scaleActivationReviewed]}.`,
    );
  }

  if (
    firstWeightReadingStatus?.length &&
    !hasField(ctx, 'firstWeightReadingStatus')
  ) {
    addField(ctx, 'firstWeightReadingStatus');
    const map = {
      readingCompletedSuccessfully:
        'First weight reading completed successfully.',
      passDisplayed: 'Device displayed PASS.',
      failDisplayed: 'Device displayed FAIL.',
      readingRepeated:
        'Reading repeated during call with medical assistant guidance.',
    };
    txt.push(...mapValuesToText(firstWeightReadingStatus, map));
  }

  if (weightUnitConfirmed) {
    const weightTextParts: string[] = [];

    if (weightUnitConfirmed.includes('weightValueConfirmed') && weightValue) {
      weightTextParts.push(`${weightValue}`);
    }

    if (
      weightUnitConfirmed.includes('unitOfMeasurementConfirmed') &&
      weightUnit
    ) {
      weightTextParts.push(`${weightUnit}`);
    }

    if (weightTextParts.length > 0) {
      txt.push(
        `Weight reading of ${weightTextParts.join(
          ' ',
        )} confirmed with patient.`,
      );
    }

    const weightAlignmentMap = {
      patientReportsReadingAlignsWithTypicalWeight: 'is',
      patientReportsReadingDoesNotAlignWithTypicalWeight: 'is not',
    };
    if (weightAlignment) {
      txt.push(
        `Patient reports reading ${weightAlignmentMap[weightAlignment]} consistent with typical weight.`,
      );
    }
  }

  if (
    programValueSingleUserEducation?.length &&
    !hasField(ctx, 'programValueSingleUserEducation')
  ) {
    addField(ctx, 'programValueSingleUserEducation');
    const map = {
      trendMonitoringExplained:
        'Importance of monitoring weight trends reviewed.',
      singleUserRequirementExplained:
        'Patient instructed that the scale is for single-user use only.',
    };

    txt.push(...mapValuesToText(programValueSingleUserEducation, map));
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare Advantage (plan selected)',
      commercial: 'Commercial',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]} based on information on file and updated in patient chart as needed.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneNumberConfirmed: 'confirmed',
      cellPhoneNumberUpdated: 'updated',
      patientReportsNoCellPhone: 'patient reports no cell phone',
    };
    txt.push(
      `Cell phone contact verified with patient ${map[cellPhoneVerification]}.`,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAndAcceptedEnrolled: 'offered and accepted',
      offeredAndDeclined: 'offered and declined',
      notEligibleNoCellPhone: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      requirementReviewed: 'monthly phone call requirement',
      valueExplained: 'program monitoring process explained',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);
    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`First monthly check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join(' ');
};
